//
// Copyright © 2026 Kotova GmbH.
//
// ZeroDesk is operated by Kotova GmbH. These are the support lines shown to users and the links to the legal
// pages on zerodesk.biz (imprint, privacy policy, terms), shared by the login pages, the billing page and the
// help popup.
//

import { getMetadata, type IntlString } from '@hcengineering/platform'

import presentation from './plugin'

/** Support runs through Kotova: e-mail and Telegram (ZeroDesk has no mailbox of its own). */
export const supportEmail = 'info@kotova.io'
export const supportMailto = `mailto:${supportEmail}`
export const supportTelegramHandle = 'kotova_io'
export const supportTelegramUrl = `https://t.me/${supportTelegramHandle}`

export interface LegalLink {
  id: 'imprint' | 'privacy' | 'terms'
  /** Short label for footers */
  label: IntlString
  /** Long label for sentences such as "By creating an account you agree to …" */
  longLabel: IntlString
  href: string
}

const defaultLandingUrl = 'https://zerodesk.biz'

/**
 * Paths of the legal pages on the landing site by language. The German pages are the binding version; every
 * other language gets the English ones. Add a language here when zerodesk.biz gets its pages.
 */
const legalPaths: Record<LegalLink['id'], Record<string, string>> = {
  imprint: { en: '/imprint/', de: '/de/impressum/' },
  privacy: { en: '/privacy/', de: '/de/datenschutz/' },
  terms: { en: '/terms/', de: '/de/agb/' }
}

export function getLandingUrl (): string {
  return (getMetadata(presentation.metadata.LandingUrl) ?? defaultLandingUrl).replace(/\/+$/, '')
}

export function getDocsUrl (): string {
  return (getMetadata(presentation.metadata.DocsUrl) ?? 'https://docs.zerodesk.biz').replace(/\/+$/, '')
}

function resolveLegalLanguage (language: string | undefined): string {
  const code = (language ?? '').toLowerCase()
  return [code, code.split('-')[0]].find((candidate) => legalPaths.imprint[candidate] !== undefined) ?? 'en'
}

/**
 * Imprint, privacy policy and terms for the given UI language ("de", "pt-br", …).
 */
export function getLegalLinks (language: string | undefined): LegalLink[] {
  const base = getLandingUrl()
  const lang = resolveLegalLanguage(language)
  return [
    {
      id: 'imprint',
      label: presentation.string.Imprint,
      longLabel: presentation.string.Imprint,
      href: base + legalPaths.imprint[lang]
    },
    {
      id: 'privacy',
      label: presentation.string.Privacy,
      longLabel: presentation.string.PrivacyPolicy,
      href: base + legalPaths.privacy[lang]
    },
    {
      id: 'terms',
      label: presentation.string.Terms,
      longLabel: presentation.string.TermsOfService,
      href: base + legalPaths.terms[lang]
    }
  ]
}
